/**
 * Fab orders (M8 plan MC-c3a; MC-c3b adds deliver and buy missing): an order is shaped like a construction site
 * (`FabOrder`, MC-0a). Pure Career → Career commands; `features/fab/feature.ts` registers them on the craft hook.
 *
 * - **Start** (`startFab`) pays the credit share of the module's price (the fab quote's `credits`, `craft/quote.ts`)
 *   and stores it in `paid`. Like buying a module ready-made, it needs an outfitting service that stocks the module's
 *   kind (MC-c6 adds the Mk III geography through the recipe's `where`). Nothing is delivered at start.
 * - **Cancel** (`cancelFab`) refunds exactly `paid`, never a re-quote, and every delivered input (and a ware or good
 *   carry's delivered tonnes) goes to the player's berth at the order's station: never deleted.
 * - **Complete** (`completeFab`) is instant, with no timer and no failure. It needs every `required` input delivered
 *   and the carry. The module goes to `career.ship.locker`: the flown ship's, which may be a fleet ship (IF-8).
 * - **A component carry (a data core) is never held by an order** (D-490): `FabOrder` keeps no component objects, so
 *   a cancelled order could not give one back. Completion takes the whole free components it still needs from the
 *   hold, then the berth at the order's station (never sealed, story or contract cargo: the fab quote's rule).
 *   `carry.have` counts for a component carry only if a later card ever sets it.
 * - **Deliver** (`deliverFab`, MC-c3b) draws each input the order still lacks, and a ware or good carry, from the hold,
 *   then the berth at the order's station; partial deliveries add up over trips. It leaves a component carry in place.
 * - **Remote berth pull** (MC-b7, T22): once unlocked (`career.json berth.remotePull.unlock`: the flown ship fits
 *   Cargo Mk III or better, or Mutual standing 25), delivery then draws the player's other open berths within
 *   `remotePull.jumps` relay jumps over the relays online now (`bfsHops`), nearest first; frozen and relocating
 *   berths are skipped, and the goods leave those berths.
 * - **Buy missing** (MC-c3b): `fabBuyPlan` lists what the station sells of the rest (the fab quote's pricing; the carry is
 *   never bought), and `bookFabBuy` books each executed strategic fill straight into `delivered` with its credits, never
 *   through the hold, so `cargoFreeT` doesn't cap it. The fab feature runs the buys through `ctx.market.commitGoods`.
 * - Delivered inputs live in their order only: no other order, the hold, the berth or the fab quote sees them.
 * - **A salvaged component** (`giveSalvage`, MC-c8) stands in for the recipe's `salvageAlt.replaces`: the named type
 *   at `minBand` or better (Wrecked never; story, sealed, contract and contraband never: `quote.ts salvageRefusal`).
 *   It leaves the hold. `FabOrder` keeps only its id (`salvageIn`), so the component itself waits in the player's
 *   berth at the order's station, held for the order: no other order, carry or quote counts it (`heldSalvage`). The
 *   replaced input leaves `required` (or the carry's `need` drops to 0), and what was already delivered of it goes
 *   to that berth. **Cancel** gives it back by letting go: the component stays in the berth, like the other
 *   delivered goods. **Complete** consumes it from the berth (or the hold, if it was taken back out); if it is gone,
 *   the order can't complete and is cancelled to start again.
 */
import { store, type BerthAccess } from '../career/berth'
import { EPSILON, withGood, withWallet, withWare, withoutComponent } from '../career/ops'
import { goodT, isGoodId, type Goods } from '../career/parts'
import { fail, formatTonnes, insufficientFunds, ok, type CmdFailure, type CmdResult } from '../career/result'
import type { BerthEntry, CargoComponent, Career, FabOrder, GoodOrWare, PatternUnlockId } from '../career/types'
import { resolveModule } from '../data/m2'
import { isModuleId, parseModuleId, type M2Data, type ModuleId, type StationDef, type StationId, type WareId } from '../data/m2Schemas'
import { bfsHops } from '../galaxy/relays'
import type { CorpFactionId } from '../strategic/types'
import type { CraftFill, CraftMarket } from './hook'
import { heldSalvage, quoteFab, salvageRefusal, type SalvageRefusal } from './quote'
import { isComponentType, offeredAt, recipeFor, type FabSystem } from './recipes'
import { isUnlocked, unlockId } from './unlocks'

declare module './hook' {
  interface CraftCommandMap {
    /** Starts a fab order for `moduleId` at `stationId` (the docked station). */
    'fab.start': { kind: 'fab.start'; stationId: StationId; moduleId: ModuleId }
    /** Cancels `orderId` (docked at its station): refunds `paid`; delivered inputs go to that station's berth. */
    'fab.cancel': { kind: 'fab.cancel'; orderId: string }
    /** Completes `orderId` (docked at its station): the module goes to the flown ship's locker. */
    'fab.complete': { kind: 'fab.complete'; orderId: string }
    /**
     * Delivers to `orderId` (docked at its station) from the hold, then the berth; with `buyMissing`, then buys what
     * the station sells of the rest straight into the order (MC-c3b). With `salvage` (a component id in the hold or
     * that berth), gives that salvaged component as the recipe's alternative input (MC-c8, `giveSalvage`) and moves
     * nothing else (PF-3); with `buyMissing` too, it gives first, then delivers and buys.
     */
    'fab.deliver': { kind: 'fab.deliver'; orderId: string; buyMissing?: boolean; salvage?: string }
  }
}

/** The open orders (empty when the Career has none). */
export function fabOrders(career: Career): readonly FabOrder[] {
  return career.fabOrders ?? []
}

/** The open order `orderId`, or null. */
export function fabOrder(career: Career, orderId: string): FabOrder | null {
  return fabOrders(career).find((o) => o.id === orderId) ?? null
}

function noOrder(orderId: string): CmdFailure {
  return fail('invalidAmount', 'That fab order is not open.', { order: orderId })
}

/** A new order id: `fab-<whole game seconds>`, then `-2`, `-3`, … while an open order already has it. */
function newOrderId(career: Career): string {
  const base = `fab-${Math.floor(career.gameTime)}`
  const taken = new Set(fabOrders(career).map((o) => o.id))
  if (!taken.has(base)) return base
  let n = 2
  while (taken.has(`${base}-${n}`)) n++
  return `${base}-${n}`
}

/** The Career with `orders` as its open orders (the key is dropped when none is left). */
function withOrders(career: Career, orders: FabOrder[]): Career {
  const next: Career = { ...career, fabOrders: orders }
  if (orders.length === 0) Reflect.deleteProperty(next, 'fabOrders')
  return next
}

/**
 * The pattern rung `moduleId` needs while it is still locked (MC-f2c), or null: a base module, or an unlocked rung
 * (`unlocks.ts isUnlocked`: in `career.patterns.unlocked`, or opened now by standing or fragments).
 */
export function lockedPattern(career: Career, moduleId: ModuleId): PatternUnlockId | null {
  const { mk, pattern } = parseModuleId(moduleId)
  if (pattern === undefined) return null
  const id = unlockId(pattern, mk === 3 ? 'mk3' : 'mk2')
  return isUnlocked(career, id) ? null : id
}

/**
 * Starts an order for `moduleId` at `station`: pays the credit share and stores it in `paid`. Refuses a module
 * without a recipe or a station that doesn't outfit its kind (`notSoldHere`), a station without outfitting
 * (`serviceUnavailable`) and too few credits (`insufficientFunds`). With a `system` (the docked system's view; null:
 * none), a recipe `offeredAt` refuses there is refused too (`notSoldHere`, `detail.reason: 'mk3Geography'`: a Mk III
 * outside a fabworks or yards system, MC-c6); undefined keeps the any-station rule, like `buyModule`. A pattern
 * module (MC-f2c) is fabricate-only and starts only once its rung is unlocked (`notSoldHere`, `detail.reason:
 * 'patternLocked'`, `detail.unlock` the rung).
 */
export function startFab(career: Career, data: M2Data, station: StationDef, moduleId: ModuleId, system?: FabSystem | null): CmdResult<Career> {
  if (!isModuleId(moduleId)) return fail('notSoldHere', `Unknown module: ${String(moduleId)}.`, { module: String(moduleId) })
  if (!station.services.includes('outfitting')) {
    return fail('serviceUnavailable', `${station.name} has no outfitting service.`, { station: station.id })
  }
  const def = resolveModule(data, moduleId)
  const recipe = recipeFor(data, moduleId)
  const quote = quoteFab(career, data, station, moduleId, null)
  if (recipe === null || quote === null) return fail('notSoldHere', `${def.name} can't be fabricated.`, { module: moduleId })
  const locked = lockedPattern(career, moduleId)
  if (locked !== null) {
    return fail('notSoldHere', `${def.name} is a pattern you have not unlocked yet.`, { module: moduleId, reason: 'patternLocked', unlock: locked })
  }
  if (!station.outfitting.includes(def.kind)) {
    return fail('notSoldHere', `${station.name} does not fabricate ${def.name}.`, { station: station.id, module: moduleId })
  }
  if (system !== undefined && !offeredAt(recipe, station, system)) {
    return fail('notSoldHere', `${def.name} is fabricated only in systems with a fabworks or a shipyard.`, {
      station: station.id,
      module: moduleId,
      reason: 'mk3Geography',
    })
  }
  const paid = quote.credits
  if (career.wallet + EPSILON < paid) return insufficientFunds(paid, career.wallet)
  const order: FabOrder = {
    id: newOrderId(career),
    moduleId,
    stationId: station.id,
    startedAt: career.gameTime,
    paid,
    required: { ...recipe.inputs },
    delivered: {},
    carry: { id: recipe.carry, need: recipe.carryQty, have: 0 },
    salvageIn: [],
  }
  return ok(withOrders(withWallet(career, -paid), [...fabOrders(career), order]))
}

/** `rec` with `key` raised by `delta` (nothing is written for an amount within `EPSILON` of zero). */
function plus<K extends string>(rec: Partial<Record<K, number>>, key: K, delta: number): Partial<Record<K, number>> {
  if (!(delta > EPSILON)) return rec
  return { ...rec, [key]: (rec[key] ?? 0) + delta }
}

function berthIsEmpty(entry: BerthEntry): boolean {
  if (entry.components.length > 0) return false
  for (const q of Object.values(entry.goods)) if ((q ?? 0) > EPSILON) return false
  for (const t of Object.values(entry.wares)) if ((t ?? 0) > EPSILON) return false
  return true
}

/** The Career with the berth at `stationId` replaced: an empty one is dropped, and an empty `storage` with it. */
function withBerthEntry(career: Career, stationId: StationId, entry: BerthEntry): Career {
  const storage = { ...career.storage }
  if (berthIsEmpty(entry)) Reflect.deleteProperty(storage, stationId)
  else storage[stationId] = entry
  const next: Career = { ...career, storage }
  if (Object.keys(storage).length === 0) Reflect.deleteProperty(next, 'storage')
  return next
}

interface Stock {
  goods: Goods
  wares: Partial<Record<WareId, number>>
}

/** `stock` with `amount` of a good or ware added. */
function addInput(stock: Stock, id: GoodOrWare, amount: number): Stock {
  if (isGoodId(id)) return { goods: plus(stock.goods, id, amount), wares: stock.wares }
  return { goods: stock.goods, wares: plus(stock.wares, id, amount) }
}

/**
 * Cancels `orderId`: refunds exactly `paid` (never a re-quote) and moves every delivered input, and a ware or good
 * carry's delivered tonnes, to the berth at the order's station. A salvaged component the order held (MC-c8) is
 * already waiting in that berth, so it comes back there: the order lets go of it. `owner` stamps a new berth (the
 * station's `StationView.owner`, as `berth.store` does). Refuses an order that isn't open (`invalidAmount`).
 */
export function cancelFab(career: Career, orderId: string, owner: CorpFactionId): CmdResult<Career> {
  const order = fabOrder(career, orderId)
  if (order === null) return noOrder(orderId)
  const entry: BerthEntry = career.storage?.[order.stationId] ?? { owner, goods: {}, wares: {}, components: [] }
  let stock: Stock = { goods: entry.goods, wares: entry.wares }
  for (const [id, t] of Object.entries(order.delivered) as [GoodOrWare, number][]) stock = addInput(stock, id, t)
  const carry = order.carry.id
  if (!isComponentType(carry)) stock = addInput(stock, carry, order.carry.have)
  const refunded = withWallet(career, order.paid)
  const stored = withBerthEntry(refunded, order.stationId, { ...entry, ...stock })
  return ok(withOrders(stored, fabOrders(career).filter((o) => o.id !== orderId)))
}

/**
 * Does `order` hold anything a cancel leaves in its station's berth (PF-3)? A delivered input, a ware or good carry's
 * delivered tonnes, or a salvaged component (MC-c8). The UI names the berth on the Cancel button when it does.
 */
export function fabOrderHolds(order: FabOrder): boolean {
  if (order.salvageIn.length > 0) return true
  if (!isComponentType(order.carry.id) && order.carry.have > EPSILON) return true
  return Object.values(order.delivered).some((t) => (t ?? 0) > EPSILON)
}

/**
 * A component that may serve as a carry: the right type, not sealed, story or contract cargo, and held by no open
 * order as its salvaged input (the quote's rule).
 */
function freeComponent(c: Readonly<CargoComponent>, type: string, held: ReadonlySet<string>): boolean {
  return c.type === type && c.sealed !== true && c.story !== true && c.contractId === undefined && !held.has(c.id)
}

/** The first input an order still lacks and how much, or null when every `required` input is delivered. */
export function fabShortfall(order: FabOrder): { id: GoodOrWare; short: number } | null {
  for (const [id, need] of Object.entries(order.required) as [GoodOrWare, number][]) {
    const short = need - (order.delivered[id] ?? 0)
    if (short > EPSILON) return { id, short }
  }
  return null
}

/** `career` with `units` free `type` components taken from the hold, then the berth at `stationId`; or the refusal. */
function takeComponentCarry(career: Career, type: string, units: number, stationId: StationId, orderId: string): CmdResult<Career> {
  const held = heldSalvage(career)
  const fromHold = career.cargo.components.filter((c) => freeComponent(c, type, held)).slice(0, units)
  let next = career
  for (const c of fromHold) next = withoutComponent(next, c.id)
  const left = units - fromHold.length
  if (left === 0) return ok(next)
  const entry = career.storage?.[stationId]
  const fromBerth = (entry?.components ?? []).filter((c) => freeComponent(c, type, held)).slice(0, left)
  if (entry === undefined || fromBerth.length < left) {
    return fail('notInCargo', `The order needs ${units} × ${type} from your hold or this station's berth.`, {
      order: orderId,
      carry: type,
      short: left - fromBerth.length,
    })
  }
  const taken = new Set(fromBerth.map((c) => c.id))
  return ok(withBerthEntry(next, stationId, { ...entry, components: entry.components.filter((c) => !taken.has(c.id)) }))
}

/**
 * `career` without the salvaged component `id` an order holds (MC-c8): taken from the berth at `stationId`, where it
 * waits, or from the hold if the player took it back out. Refuses (`notInCargo`, `detail.reason: 'salvageMissing'`)
 * when it is in neither.
 */
function consumeSalvage(career: Career, id: string, stationId: StationId, orderId: string): CmdResult<Career> {
  const entry = career.storage?.[stationId]
  if (entry !== undefined && entry.components.some((c) => c.id === id)) {
    return ok(withBerthEntry(career, stationId, { ...entry, components: entry.components.filter((c) => c.id !== id) }))
  }
  if (career.cargo.components.some((c) => c.id === id)) return ok(withoutComponent(career, id))
  return fail('notInCargo', "The salvaged component given to this order is no longer in this station's berth or your hold. Cancel the order to start again.", {
    order: orderId,
    id,
    reason: 'salvageMissing',
  })
}

/**
 * Completes `orderId` once every `required` input and the carry are delivered: the module goes to the flown ship's
 * locker (`career.ship.locker`). A component carry is taken whole from the hold, then the berth at the order's
 * station, and a salvaged component the order holds (MC-c8) is consumed. Refuses an order that isn't open
 * (`invalidAmount`) or that lacks an input, the carry or its salvaged component (`notInCargo`).
 */
export function completeFab(career: Career, orderId: string): CmdResult<Career> {
  const order = fabOrder(career, orderId)
  if (order === null) return noOrder(orderId)
  const short = fabShortfall(order)
  if (short !== null) {
    return fail('notInCargo', `The order still needs ${formatTonnes(short.short)} t of ${short.id}.`, { order: orderId, input: short.id, short: short.short })
  }
  const carry = order.carry
  const missing = carry.need - carry.have
  let next = career
  if (isComponentType(carry.id)) {
    const units = Math.max(0, Math.ceil(missing - EPSILON))
    if (units > 0) {
      const r = takeComponentCarry(career, carry.id, units, order.stationId, orderId)
      if (!r.ok) return r
      next = r.value
    }
  } else if (missing > EPSILON) {
    return fail('notInCargo', `The order still needs ${formatTonnes(missing)} t of ${carry.id}.`, { order: orderId, carry: carry.id, short: missing })
  }
  for (const id of order.salvageIn) {
    const r = consumeSalvage(next, id, order.stationId, orderId)
    if (!r.ok) return r
    next = r.value
  }
  const closed = withOrders(next, fabOrders(next).filter((o) => o.id !== orderId))
  return ok({ ...closed, ship: { ...closed.ship, locker: [...closed.ship.locker, order.moduleId] } })
}

/** `rec` with `key` lowered by `delta`; a key left within `EPSILON` of zero is dropped. */
function minus<K extends string>(rec: Partial<Record<K, number>>, key: K, delta: number): Partial<Record<K, number>> {
  const left = (rec[key] ?? 0) - delta
  const next = { ...rec }
  if (left > EPSILON) next[key] = left
  else Reflect.deleteProperty(next, key)
  return next
}

/** Tonnes of a good or ware in the hold. */
function holdT(career: Career, id: GoodOrWare): number {
  return isGoodId(id) ? goodT(career.cargo.parts, id) : career.cargo.wares[id]
}

/** Tonnes of a good or ware in `entry` (a berth). */
function berthT(entry: BerthEntry | undefined, id: GoodOrWare): number {
  if (entry === undefined) return 0
  return isGoodId(id) ? goodT(entry.goods, id) : (entry.wares[id] ?? 0)
}

/** `entry` with `amount` tonnes of a good or ware taken out. */
function berthLess(entry: BerthEntry, id: GoodOrWare, amount: number): BerthEntry {
  if (isGoodId(id)) return { ...entry, goods: minus(entry.goods, id, amount) }
  return { ...entry, wares: minus(entry.wares, id, amount) }
}

/** `career` with `amount` tonnes of a good or ware taken out of the hold. */
function holdLess(career: Career, id: GoodOrWare, amount: number): Career {
  return isGoodId(id) ? withGood(career, id, -amount) : withWare(career, id, -amount)
}

/** Tonnes each open input of `order` still lacks (inputs within `EPSILON` of delivered are left out). */
export function fabMissing(order: FabOrder): Partial<Record<GoodOrWare, number>> {
  const out: Partial<Record<GoodOrWare, number>> = {}
  for (const [id, need] of Object.entries(order.required) as [GoodOrWare, number][]) {
    const short = need - (order.delivered[id] ?? 0)
    if (short > EPSILON) out[id] = short
  }
  return out
}

// ---- Remote berth pull (MC-b7, T22) --------------------------------------------------------

/** What remote pull reads of the data: `career.json berth.remotePull`. */
export type RemotePullData = Pick<M2Data, 'career'>

/**
 * What remote pull reads of the world (MC-b7): the relay graph online now and each berth station's system and
 * access. The fab feature builds it from the Reach runtime and the strategic client's cached system view.
 */
export interface RemoteBerthWorld {
  /** Every system id (the jump search's nodes; any fixed order). */
  readonly systems: readonly string[]
  /** The relays online now, as system id pairs. */
  readonly links: readonly (readonly [string, string])[]
  /** The system of the station a berth is keyed by, or null when unknown (that berth is skipped). */
  systemOf(stationId: StationId): string | null
  /** The berth's access (`career/berth.ts berthAccess`): a frozen or relocating berth is skipped. */
  access(stationId: StationId, entry: BerthEntry, career: Career): BerthAccess
}

/** Remote pull's inputs for `deliverFab`: the data and the world. */
export interface RemotePull {
  readonly data: RemotePullData
  readonly world: RemoteBerthWorld
}

/** A berth remote pull may draw from, and its relay jumps from the order's station (0: the same system). */
export interface RemoteBerth {
  stationId: StationId
  jumps: number
}

/**
 * Is remote pull unlocked (U8)? The flown ship fits `unlock.module` or a better Mk of its kind (`career.ship` is
 * always the flown ship), or the pilot's standing with `unlock.faction` is at least `unlock.standing`.
 */
export function remotePullUnlocked(career: Career, data: RemotePullData): boolean {
  const { unlock } = data.career.berth.remotePull
  const want = parseModuleId(unlock.module)
  for (const id of Object.values(career.ship.fitted)) {
    if (!isModuleId(id)) continue
    const m = parseModuleId(id)
    if (m.kind === want.kind && m.mk >= want.mk) return true
  }
  const standings: Partial<Record<string, number>> = career.standings
  return (standings[unlock.faction] ?? 0) >= unlock.standing
}

/**
 * The player's other berths remote pull may draw from for an order at `stationId`: unlocked only, within
 * `remotePull.jumps` relay jumps over the relays online now (`bfsHops`), open (frozen and relocating berths are
 * skipped), nearest first, then by station id. The order's own berth is never listed (`deliverFab` draws it first).
 * Empty when locked, or when the order's station has no known system.
 */
export function remoteBerths(career: Career, stationId: StationId, pull: RemotePull): RemoteBerth[] {
  const storage = career.storage
  if (storage === undefined || !remotePullUnlocked(career, pull.data)) return []
  const { world } = pull
  const index = new Map(world.systems.map((id, i) => [id, i]))
  const home = world.systemOf(stationId)
  const from = home === null ? undefined : index.get(home)
  if (from === undefined) return []
  const edges: [number, number][] = []
  for (const [a, b] of world.links) {
    const ia = index.get(a)
    const ib = index.get(b)
    if (ia !== undefined && ib !== undefined) edges.push([ia, ib])
  }
  const hops = bfsHops(world.systems.length, edges, from)
  const max = pull.data.career.berth.remotePull.jumps
  const out: RemoteBerth[] = []
  for (const id of Object.keys(storage)) {
    if (id === stationId) continue
    const sys = world.systemOf(id)
    const at = sys === null ? undefined : index.get(sys)
    const jumps = at === undefined ? -1 : hops[at]
    if (jumps < 0 || jumps > max) continue
    if (world.access(id, storage[id], career).kind !== 'open') continue
    out.push({ stationId: id, jumps })
  }
  return out.sort((a, b) => a.jumps - b.jumps || (a.stationId < b.stationId ? -1 : a.stationId > b.stationId ? 1 : 0))
}

/**
 * Delivers to `orderId` what the hold, then the player's berth at the order's station, holds of each input it still
 * lacks, and of a ware or good carry (into `carry.have`). With `remote` (MC-b7) and remote pull unlocked, it then
 * draws the player's other open berths within `remotePull.jumps` relay jumps, nearest first (`remoteBerths`); the
 * goods leave those berths. Partial deliveries are fine: an order can be filled over several trips. **A component
 * carry (a data core) stays where it is** (D-490): completion takes it. Nothing is bought here (`fabBuyPlan` +
 * `bookFabBuy` do buy missing). Refuses an order that isn't open (`invalidAmount`) and a delivery that would move
 * nothing (`notInCargo`, `detail.reason: 'nothingToDeliver'`). Docking is the caller's check.
 */
export function deliverFab(career: Career, orderId: string, remote?: RemotePull | null): CmdResult<Career> {
  const order = fabOrder(career, orderId)
  if (order === null) return noOrder(orderId)
  let next = career
  const berths: { id: StationId; entry: BerthEntry; moved: boolean }[] = []
  const local = career.storage?.[order.stationId]
  if (local !== undefined) berths.push({ id: order.stationId, entry: local, moved: false })
  if (remote !== undefined && remote !== null) {
    for (const b of remoteBerths(career, order.stationId, remote)) {
      const entry = career.storage?.[b.stationId]
      if (entry !== undefined) berths.push({ id: b.stationId, entry, moved: false })
    }
  }
  let delivered = order.delivered
  let moved = 0
  /** Takes up to `want` tonnes of `id` from the hold, then each berth in order; returns the tonnes taken. */
  const draw = (id: GoodOrWare, want: number): number => {
    const fromHold = Math.min(want, Math.max(0, holdT(next, id)))
    let took = 0
    if (fromHold > EPSILON) {
      next = holdLess(next, id, fromHold)
      took += fromHold
    }
    for (const b of berths) {
      const fromBerth = Math.min(want - took, Math.max(0, berthT(b.entry, id)))
      if (!(fromBerth > EPSILON)) continue
      b.entry = berthLess(b.entry, id, fromBerth)
      b.moved = true
      took += fromBerth
    }
    moved += took
    return took
  }
  for (const [id, short] of Object.entries(fabMissing(order)) as [GoodOrWare, number][]) {
    delivered = plus(delivered, id, draw(id, short))
  }
  let carry = order.carry
  const carryId = carry.id
  if (!isComponentType(carryId)) {
    const short = carry.need - carry.have
    if (short > EPSILON) carry = { ...carry, have: carry.have + draw(carryId, short) }
  }
  if (!(moved > EPSILON)) {
    return fail('notInCargo', 'Nothing in your hold or your berths in reach that the order still needs.', { order: orderId, reason: 'nothingToDeliver' })
  }
  for (const b of berths) if (b.moved) next = withBerthEntry(next, b.id, b.entry)
  const updated: FabOrder = { ...order, delivered, carry }
  return ok(withOrders(next, fabOrders(next).map((o) => (o.id === orderId ? updated : o))))
}

const SALVAGE_REFUSAL_TEXT: Readonly<Record<SalvageRefusal, string>> = {
  wrongComponent: 'That component is not the kind this recipe takes.',
  band: 'That component is too worn for this recipe.',
  contract: 'Contract cargo belongs to its contract and',
  story: 'A story item belongs to its mission and',
  sealed: 'Sealed cargo',
  contraband: 'Contraband',
}

/**
 * Gives salvaged component `componentId` (in the hold, or the berth at the order's station) to `orderId` as its
 * recipe's alternative input (MC-c8, `salvageAlt`): it stands in for `replaces`. The component leaves the hold and
 * waits in that berth, held for the order (`salvageIn`); the replaced input leaves `required` (or the carry's `need`
 * drops to 0), and whatever was already delivered of it goes to the berth, never deleted. `owner` stamps a new
 * berth, as `cancelFab`. Refuses an order that isn't open, a recipe without an alternative (`detail.reason:
 * 'noSalvageAlt'`), an order that already holds one (`'salvageGiven'`), a component another order holds
 * (`'salvageHeld'`), the wrong type or a band below `minBand` (Wrecked always; `'wrongComponent'`, `'band'`), all
 * `invalidAmount`; contract, story, sealed and contraband cargo (`serviceUnavailable`, the berth's reasons); a
 * component in neither place (`notInCargo`); and a berth over its component cap (`cargoFull`, `berth.store`'s rule).
 */
export function giveSalvage(career: Career, data: M2Data, orderId: string, componentId: string, owner: CorpFactionId): CmdResult<Career> {
  const order = fabOrder(career, orderId)
  if (order === null) return noOrder(orderId)
  if (order.salvageIn.length > 0) return fail('invalidAmount', 'This order already holds its salvaged component.', { order: orderId, reason: 'salvageGiven' })
  const alt = recipeFor(data, order.moduleId)?.salvageAlt
  const replaces = alt?.replaces
  const onOrder = replaces !== undefined && (replaces === order.carry.id ? order.carry.need > EPSILON : replaces in order.required)
  if (alt === undefined || !onOrder) {
    return fail('invalidAmount', `${resolveModule(data, order.moduleId).name} takes no salvaged component.`, { order: orderId, reason: 'noSalvageAlt' })
  }
  const stationId = order.stationId
  const inHold = career.cargo.components.find((c) => c.id === componentId)
  const c = inHold ?? career.storage?.[stationId]?.components.find((x) => x.id === componentId)
  if (c === undefined) {
    return fail('notInCargo', "That component is not in your hold or this station's berth.", { order: orderId, id: componentId })
  }
  if (heldSalvage(career).has(componentId)) {
    return fail('invalidAmount', 'Another fab order already holds that component.', { order: orderId, id: componentId, reason: 'salvageHeld' })
  }
  const refusal = salvageRefusal(c, alt, data.salvage.bands)
  if (refusal === 'wrongComponent' || refusal === 'band') {
    return fail('invalidAmount', SALVAGE_REFUSAL_TEXT[refusal], { order: orderId, id: componentId, reason: refusal })
  }
  if (refusal !== null) {
    return fail('serviceUnavailable', `${SALVAGE_REFUSAL_TEXT[refusal]} can't stand in for a fab input.`, { order: orderId, id: componentId, reason: refusal })
  }
  let next = career
  if (inHold !== undefined) {
    const parked = store(career, data, stationId, { components: [componentId] }, owner)
    if (!parked.ok) return parked
    next = parked.value
  }
  const entry: BerthEntry = next.storage?.[stationId] ?? { owner, goods: {}, wares: {}, components: [] }
  let stock: Stock = { goods: entry.goods, wares: entry.wares }
  let updated: FabOrder
  if (alt.replaces === order.carry.id) {
    if (!isComponentType(order.carry.id)) stock = addInput(stock, order.carry.id, order.carry.have)
    updated = { ...order, carry: { ...order.carry, need: 0, have: 0 }, salvageIn: [componentId] }
  } else {
    const id = alt.replaces as GoodOrWare
    stock = addInput(stock, id, order.delivered[id] ?? 0)
    const required = { ...order.required }
    const delivered = { ...order.delivered }
    Reflect.deleteProperty(required, id)
    Reflect.deleteProperty(delivered, id)
    updated = { ...order, required, delivered, salvageIn: [componentId] }
  }
  next = withBerthEntry(next, stationId, { ...entry, ...stock })
  return ok(withOrders(next, fabOrders(next).map((o) => (o.id === orderId ? updated : o))))
}

/** One buy-missing line: `qty` tonnes of `id` at the station's quote, `credits` under tariff (rounded). */
export interface FabBuyLine {
  id: GoodOrWare
  qty: number
  credits: number
}

/**
 * What buy missing would buy for `orderId` now: each input the order still lacks that the order's station sells,
 * clamped to its stock (`market.quote(…, 'buy')`, the fab quote's pricing). The carry is never bought. Empty when the
 * order isn't open or the station sells none of it.
 */
export function fabBuyPlan(career: Career, orderId: string, market: CraftMarket): FabBuyLine[] {
  const order = fabOrder(career, orderId)
  if (order === null) return []
  const out: FabBuyLine[] = []
  for (const [id, short] of Object.entries(fabMissing(order)) as [GoodOrWare, number][]) {
    const q = market.quote(order.stationId, id, short, 'buy')
    if (q === null || !(q.qty > EPSILON)) continue
    out.push({ id, qty: Math.min(q.qty, short), credits: Math.round(q.total) })
  }
  return out
}

/**
 * Books an executed buy-missing fill (`CraftMarket.commitGoods`' `book`): the tonnes go **straight into the order's
 * `delivered`**, never the hold, so the hold's free tonnes don't cap it, and the credits leave the wallet in the same
 * Career. Refuses an order that isn't open (`invalidAmount`), a bad fill (`invalidAmount`) and too few credits
 * (`insufficientFunds`).
 */
export function bookFabBuy(career: Career, orderId: string, id: GoodOrWare, fill: CraftFill): CmdResult<Career> {
  const order = fabOrder(career, orderId)
  if (order === null) return noOrder(orderId)
  if (!(Number.isFinite(fill.qty) && fill.qty > 0) || !(Number.isFinite(fill.credits) && fill.credits >= 0)) {
    return fail('invalidAmount', 'That fill is not a valid buy.', { order: orderId, qty: fill.qty, credits: fill.credits })
  }
  if (career.wallet + EPSILON < fill.credits) return insufficientFunds(fill.credits, career.wallet)
  const updated: FabOrder = { ...order, delivered: plus(order.delivered, id, fill.qty) }
  const paid = withWallet(career, -fill.credits)
  return ok(withOrders(paid, fabOrders(paid).map((o) => (o.id === orderId ? updated : o))))
}
